// React & Hooks
import { forwardRef, memo, useCallback, useEffect, useImperativeHandle, useState } from "react";

// Helpers
import { useTranslate } from "helpers";

// API
import { getFleetVehicleDriversAPI } from "services/api/fleet/vehicles/drivers/api";
import { FleetVehicleDriverRow } from "services/api/fleet/vehicles/drivers/types";
import { DataWithPagination } from "services/api";
import { initialDataWithPagination } from "services/api/consts";

// Search & Pagination
import { Pagination } from "services/search";
import { useSelectedItemsChanges } from "ui/components/Table/hooks";

// UI Components
import { NoResults } from "ui/components/NoResults";

// Page Titles
// Local Components & Types
import { VehicleDriverCardProps, VehicleDriverRef } from "./types";
import VehicleDriverSearchResults from "./components/VehicleDriverSearchResults/VehicleDriverSearchResults";
import VehicleDriverEditCard from "../VehicleDriverEditCard/VehicleDriverEditCard";

/**
 * Vozači vozila. Kartica je samo tablica — pretraga i unos stoje u zaglavlju widgeta
 * (`VehicleDriverHeader`), kao kod dokumenata, troškova i ponuda.
 */
const VehicleDriverCard = forwardRef<VehicleDriverRef, VehicleDriverCardProps>((props, ref) => {
  const { fleetVehicleDetail } = props;

  const { t } = useTranslate();

  const [searchResults, setSearchResults] = useState<
    DataWithPagination<FleetVehicleDriverRow>
  >(initialDataWithPagination);

  const [driverId, setDriverId] = useState<string>("");
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showAddNewModal, setShowAddNewModal] = useState<boolean>(false);

  useEffect(() => {
    if (fleetVehicleDetail.id)
      fetchSearchResult(searchResults.pagination, fleetVehicleDetail.id);
  }, [fleetVehicleDetail.id]);

  const fetchSearchResult = async (
    pagination: Pagination,
    vehicleId: string
  ) => {
    setIsLoading(true);
    try {
      const results = await getFleetVehicleDriversAPI(pagination, vehicleId);

      setSearchResults(results);
    } catch {
      setSearchResults(initialDataWithPagination);
      setIsLoading(false);
      return;
    }
    setIsLoading(false);
  };

  const handleRowClick = useCallback(async (id: string) => {
    setDriverId(id);
    setShowAddNewModal(true);
  }, []);

  const openAddNew = useCallback(() => {
    setDriverId("");
    setShowAddNewModal(true);
  }, []);

  useImperativeHandle(ref, () => ({ openAddNew }), [openAddNew]);

  const handleSelectedChange = useSelectedItemsChanges(
    selectedRows,
    setSelectedRows
  );

  const handlePaginationChange = (newPagination: Pagination) => {
    if (fleetVehicleDetail.id)
      fetchSearchResult(searchResults.pagination, fleetVehicleDetail.id);
  };

  const handleEditSuccess = useCallback(() => {
    setShowAddNewModal(false);

    if (fleetVehicleDetail.id)
      fetchSearchResult(searchResults.pagination, fleetVehicleDetail.id);
  }, [fetchSearchResult]);

  if (!fleetVehicleDetail.id) return <></>;

  return (
    <>
      <div className="h-full">
        <div className="m-[-1px]">
          {!isLoading && searchResults.pagination.totalPages === 0 ? (
            <NoResults message={t("admin.fleet.vehicle.driver.search.noresults")} />
          ) : (
            <VehicleDriverSearchResults
              searchResults={searchResults}
              selectedRows={selectedRows}
              isLoading={isLoading}
              onRowClick={handleRowClick}
              onPaginationChange={handlePaginationChange}
              onSelectedChange={handleSelectedChange}
            />
          )}
        </div>
      </div>

      {showAddNewModal && (
        <VehicleDriverEditCard
          show={showAddNewModal}
          vehicleId={fleetVehicleDetail.id}
          driverId={driverId}
          onSuccess={handleEditSuccess}
          onClose={() => setShowAddNewModal(false)}
        />
      )}
    </>
  );
});

export default memo(VehicleDriverCard);
